/* "Send to phone": the finished report's data as one small file the runner carries to another
 * device themselves (AirDrop, email, a cloud drive). No server is involved. It holds the built
 * dataset (run numbers, no GPS routes, never the original exports), the runner's corrections and
 * the name as it shows on the poster (edited, or '' if they hid it). */
import type { Dataset } from '../import/types';

const FORMAT = 'mileposts-report', VERSION = 1;

/* name: the name shown on the poster; null when the file predates names (use the export's). */
export interface ReportFile { dataset: Dataset; notRace: number[]; name: string | null }

/* 'json' for downloads; 'txt' for the share sheet, because browsers only share certain file types
 * (Chrome refuses .json with "Permission denied" but accepts plain text). Same contents either way. */
export function makeReportFile(dataset: Dataset, notRace: number[], name: string, as: 'json' | 'txt' = 'json'): File {
  const day = new Date().toISOString().slice(0, 10);
  const body = JSON.stringify({ format: FORMAT, version: VERSION, created: new Date().toISOString(), name, notRace, dataset });
  return new File([body], `Mileposts report ${day}.${as}`, { type: as === 'json' ? 'application/json' : 'text/plain' });
}

export const isReportFileName = (name: string) => /\.(json|txt)$/i.test(name);

export async function readReportFile(f: File): Promise<ReportFile> {
  let j: { format?: string; version?: number; notRace?: unknown; name?: unknown; dataset?: Dataset };
  try { j = JSON.parse(await f.text()); } catch { throw new Error(`${f.name} isn't a Mileposts report file.`); }
  if (j.format !== FORMAT || !j.dataset || !Array.isArray(j.dataset.runs)) throw new Error(`${f.name} isn't a Mileposts report file.`);
  if ((j.version ?? 0) > VERSION) throw new Error('This report file was made by a newer version of Mileposts. Reload the page and try again.');
  return {
    dataset: j.dataset,
    notRace: Array.isArray(j.notRace) ? j.notRace.filter((x): x is number => typeof x === 'number') : [],
    name: typeof j.name === 'string' ? j.name.slice(0, 40) : null,
  };
}
